/**
 * Types for AI Extraction Review feature
 * Corresponds to phr.ai_extraction_log and phr.ai_extracted_suggestion tables
 */

export type SuggestionType = 'diagnosis' | 'measurement';
export type SuggestionStatus = 'pending' | 'approved' | 'rejected' | 'superseded';
export type ExtractionType = 'dx' | 'meas' | 'labs' | 'allergy' | 'all';
export type SourceType = 'ccda' | 'lab_pdf';

export interface AIExtractionLog {
  id: number;
  extraction_type: ExtractionType;
  source_type: SourceType;
  extraction_timestamp: number;
  is_reviewed: boolean;
  total_suggestions: number;
  approved_count: number;
  rejected_count: number;
}

export interface SourceImageExcerpt {
  page: number;
  bbox: number[];   // normalized [x0, y0, x1, y1], top-left origin
  image: string;    // data:image/png;base64,... crop of the source region
  text: string;     // verbatim source cell text
}

export interface AIExtractedSuggestion {
  id: number;
  suggestion_type: SuggestionType;
  status: SuggestionStatus;
  source_text_excerpts: string[];
  source_image_excerpts?: SourceImageExcerpt[];  // text-layer-grounded source-region crops (visual confirmation)
  source_vendor?: string | null;                 // result-provenance vendor (boston_heart / omegaquant / …)
  created_on: number;
  reviewed_on: number | null;
  reviewed_by: string | null;
  rejection_category?: string | null;
  rejection_reason?: string | null;

  // For diagnoses
  slot_name?: string;
  /** Curated questionnaire display_name from the KB, e.g. "hx.nv: carpal tunnel syndrome".
   *  Prefix-stripped for display. Prefer this over slot_name, which is a KB identifier
   *  and reads as truncated when de-underscored ("... syndrome minim"). */
  display_name?: string;
  questionnaire_name?: string;
  question_stem?: string;
  proposed_answer?: string;
  proposed_answer_fields?: Record<string, any>;
  existing_answer?: string | null;
  existing_answer_date?: number | null;

  // For measurements
  measurement_name?: string;
  measurement_value?: string;
  measurement_unit?: string;
  measurement_date?: number;
  value_range?: { min: number | null; max: number | null };
  is_abnormal?: boolean;
}

export interface AISuggestionsResponse {
  suggestions: AIExtractedSuggestion[];
  extraction_log: AIExtractionLog | null;
}

/**
 * How much of one source file the extractor actually read.
 *
 * A measurement count is meaningless without the extent it was drawn from, so the backend
 * sends them together and a client branches on `complete` — never by parsing `errors` prose.
 * Anchor (CAPA F272, 2026-08-06): a 94-page lab bundle was read as its first 20 pages and this
 * screen reported "extracted 55 measurements" in green, because incompleteness had no field of
 * its own. `pages_total` is null when the file failed before it could be paged.
 */
export interface SourceCoverage {
  file: string;
  pages_total: number | null;
  pages_read: number;
  pages_unread: number[];
  complete: boolean;
  batches?: number;
  batches_failed?: number;
  error?: string;
}

export interface ExtractDiagnosesResponse {
  extraction_log_id: number;
  total_suggestions: number;
  diagnosis_count: number;
  measurement_count: number;
  /** Per source file; absent on paths that do not page a source (e.g. CCD extraction). */
  coverage?: SourceCoverage[] | null;
  /** True only when every source file was read whole. Null when coverage does not apply. */
  complete?: boolean | null;
  allergy_count?: number;
  allergy_list?: AllergyEntry[];
  skipped_allergies?: SkippedAllergyEntry[];
  questionnaire_list?: any[];
  diagnosis_list?: string[];
  measurement_list?: any[];
  errors?: string[] | null;
  message: string;
}

export interface AllergyEntry {
  name: string;
  code: string;
  code_system: string;
  allergy_type: string;
  reaction: string;
  criticality: string;
  clinical_status: string;
  verification_status: string;
  onset: string;
}

export interface SkippedAllergyEntry {
  name: string;
  reason: string;
  reaction?: string;
  resolved_name?: string;
}

export interface ApproveSuggestionsRequest {
  approved_ids: number[];
  rejected_ids: number[];
  /** structured reject taxonomy code per suggestion id (see *_REJECTION_CATEGORIES) */
  rejection_categories?: Record<number, string>;
  /** free-text note per suggestion id (used for the 'other' category) */
  rejection_reasons?: Record<number, string>;
  /** correction per suggestion id, e.g. the value/date it should have been */
  corrections?: Record<number, { corrected_answer?: string }>;
  /**
   * Reviewer-supplied specimen collection date per suggestion id, 'YYYY-MM-DD'.
   *
   * A measurement the extractor could not date is REFUSED at the write rather than filed
   * as of today — a stale value dated today becomes the patient's current value. This is
   * the recourse: the reviewer reads the real "Date Collected" off the source PDF. The
   * server validates it (parseable, not in the future) and records who set it.
   */
  date_overrides?: Record<number, string>;
}

/**
 * Structured reject taxonomy. Codes are stable identifiers that must stay in sync
 * with the backend (ai_extraction_service.py MEAS_/DX_REJECTION_CATEGORIES) and the
 * /ai-feedback/export CSV. They characterize why a suggestion was rejected so the
 * downloadable feedback log can drive model improvement.
 */
export interface RejectionCategory {
  code: string;
  label: string;
  /** whether to show the optional "correct value" field for this category */
  allowsCorrection: boolean;
}

export const MEAS_REJECTION_CATEGORIES: RejectionCategory[] = [
  { code: 'wrong_parameter', label: 'Wrong parameter', allowsCorrection: true },
  { code: 'wrong_value', label: 'Wrong value', allowsCorrection: true },
  { code: 'wrong_date', label: 'Wrong date', allowsCorrection: true },
  { code: 'wrong_unit', label: 'Wrong unit', allowsCorrection: true },
  { code: 'not_a_measurement', label: 'Not a measurement', allowsCorrection: false },
  { code: 'other', label: 'Other', allowsCorrection: false },
];

export const DX_REJECTION_CATEGORIES: RejectionCategory[] = [
  { code: 'wrong_diagnosis', label: 'Wrong diagnosis', allowsCorrection: false },
  { code: 'wrong_mapping', label: 'Wrong mapping', allowsCorrection: true },
  { code: 'other', label: 'Other', allowsCorrection: false },
];

export interface ApproveSuggestionsResponse {
  approved_count: number;
  rejected_count: number;
  created_measurements: number[];
  created_questionnaire_responses: number[];
  errors: string[] | null;
}

export interface UploadLabPdfsResponse {
  uploaded_files: string[];
  file_count: number;
  storage_path: string;
  extraction_result?: ExtractDiagnosesResponse;
}

export interface ExtractLabsResponse {
  extraction_log_id: number;
  total_suggestions: number;
  measurement_count: number;
  message: string;
}
